import { Component, input } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FileUploadFieldComponent } from '../../../atoms/forms/file-upload-field/file-upload-field.component';
import { ErrorFieldComponent } from '../../../atoms/forms/error-field/error-field.component';
import { ErrorTypeLabels, DEFAULT_ERROR_TYPE_LABELS } from '../../../shared/interfaces/error-messages.interface';

@Component({
  selector: 'htx-file-upload-field-with-error',
  imports: [CommonModule, FileUploadFieldComponent, ErrorFieldComponent],
  templateUrl: './file-upload-field-with-error.component.html',
  styleUrl: './file-upload-field-with-error.component.scss',
  standalone: true
})
export class FileUploadFieldWithErrorComponent {
  /** The FormControl that manages the file upload value and validation state */
  ctrl = input.required<AbstractControl>();

  /** Icon name to display (default: 'cloud_upload') */
  uploadIcon = input<string>('cloud_upload');

  /** Custom text for main upload label */
  uploadText = input<string>();

  /** Custom text for drag & drop hint */
  dragDropText = input<string>();

  /** Custom text for change file hint */
  changeText = input<string>();

  /** Maximum file size in bytes (optional) */
  maxFileSize = input<number>();

  /** Accepted file types (e.g., 'application/pdf' or '.pdf,image/*') */
  acceptedFileTypes = input<string>();

  /** Custom error messages for different validation types */
  errorMessages = input<ErrorTypeLabels>(DEFAULT_ERROR_TYPE_LABELS);

  /**
   * Returns the control as a FormControl for direct access to its properties and methods.
   */
  getControl(): FormControl {
    return this.ctrl() as FormControl;
  }

  /**
   * Validates file size against maxFileSize
   */
  validateFileSize(file: File): boolean {
    if (!this.maxFileSize()) {
      return true;
    }

    if (file.size > this.maxFileSize()!) {
      this.getControl().setErrors({ fileTooLarge: true });
      return false;
    }

    // Clear fileTooLarge error if file is now valid
    const currentErrors = this.getControl().errors;
    if (currentErrors && 'fileTooLarge' in currentErrors) {
      delete currentErrors['fileTooLarge'];
      if (Object.keys(currentErrors).length === 0) {
        this.getControl().setErrors(null);
      } else {
        this.getControl().setErrors(currentErrors);
      }
    }

    return true;
  }

  /**
   * Formats bytes to human-readable size string
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Get formatted error message for fileTooLarge error
   */
  getFileSizeErrorMessage(): string {
    const messages = this.errorMessages();
    const message = messages['fileTooLarge'] || 'File is too large. Maximum size: {max}';

    if (this.maxFileSize()) {
      return message.replace('{max}', this.formatFileSize(this.maxFileSize()!));
    }

    return message;
  }
}
